import { z } from 'zod';
/**
 * Contrats de la CONFIGURATION GLOBALE de l'application. Source unique front + back.
 *
 * `app_settings` est une table mono-ligne (singleton) qui porte les réglages globaux :
 * la bascule `aiEnabled` (Incrément B — coupe-circuit IA) et le `appName` (LOT 3 — nom
 * du site éditable par le superadmin, stocké en base et non en variable d'env).
 *
 * - `appConfigSchema`          : état LU par la SPA (`GET /api/app-config`), lisible
 *   par TOUT rôle authentifié (masquage des entrées IA, nom du site dans l'UI).
 * - `updateAppSettingsSchema`  : corps du PATCH SUPERADMIN (`PATCH /api/app-settings`).
 *   Chaque champ est OPTIONNEL (PATCH partiel : bascule IA seule OU renommage seul).
 *   AUCUN `.transform` (piège boot-crash Zod v4 : `z.toJSONSchema` au démarrage) — le
 *   trim du nom est fait dans le handler ; garde `route-json-schema.test.ts`.
 */
/** Longueur maximale du nom de site (borne partagée front/back). */
export const APP_NAME_MAX_LENGTH = 120;
/** Longueur maximale de l'URL de passerelle (borne partagée front/back). */
export const ACCESS_GATEWAY_URL_MAX_LENGTH = 2048;
/**
 * Agrandissement maximal (en %) d'une petite photo dans la visionneuse — bornes et
 * défaut partagés front/back.
 *
 * Sur grand écran, une image PLUS PETITE que la fenêtre est agrandie au repos jusqu'à
 * ce plafond (100 % = taille d'origine, aucun agrandissement ; 150 % = jusqu'à ×1,5).
 * Plancher 100 % : en dessous, le plafond « mordrait » aussi sur les GRANDES images
 * (celles réduites pour tenir dans le cadre), cassant le fit « contain ».
 */
export const SMALL_IMAGE_UPSCALE_MIN_PERCENT = 100;
export const SMALL_IMAGE_UPSCALE_MAX_PERCENT = 400;
export const SMALL_IMAGE_UPSCALE_DEFAULT_PERCENT = 150;
/**
 * État global exposé à la SPA. Le schéma est volontairement extensible (d'autres
 * réglages globaux pourront s'y ajouter).
 */
export const appConfigSchema = z.object({
    /** Les fonctions LLM/IA sont-elles activées ? (défaut `true` = comportement actuel). */
    aiEnabled: z.boolean(),
    /** Nom du site affiché dans l'UI (titre d'onglet, à-propos, PWA). Défaut `'xphoto'`. */
    appName: z.string().min(1).max(APP_NAME_MAX_LENGTH),
    /**
     * Passerelle d'authentification (ADR-0008) : URL de l'app tierce qui identifie les
     * visiteurs, ou `null` (défaut) = aucune → écran de connexion habituel.
     *
     * Également exposée par `/api/health` (PUBLIC) : c'est le visiteur NON authentifié
     * qui en a besoin pour être renvoyé, or `/api/app-config` est derrière la garde.
     * Les deux surfaces lisent la MÊME colonne (`app_settings.access_gateway_url`,
     * migration 0038) — la base reste la source unique. Ici, elle sert au formulaire
     * superadmin (lecture + écho du PATCH).
     */
    accessGatewayUrl: z.string().nullable(),
    /**
     * Agrandissement maximal (%) des petites photos dans la visionneuse (cf.
     * `SMALL_IMAGE_UPSCALE_*`). Défaut `150`. Lu par la SPA pour piloter le plafond de
     * zoom au repos (`computeZoomBounds`).
     */
    smallImageUpscalePercent: z
        .number()
        .int()
        .min(SMALL_IMAGE_UPSCALE_MIN_PERCENT)
        .max(SMALL_IMAGE_UPSCALE_MAX_PERCENT),
    /**
     * JETON DE CONCURRENCE (verrouillage optimiste) : `app_settings.updated_at` en ISO 8601.
     * Le formulaire superadmin le renvoie en `expectedUpdatedAt` lors du PATCH ; si la ligne
     * a bougé entre-temps, l'écriture est REFUSÉE (409 `STALE_UPDATE`) au lieu d'écraser
     * silencieusement le réglage d'un autre éditeur.
     *
     * `.optional()` (champ ADDITIF) : les consommateurs antérieurs restent valides, et un
     * client qui l'omet retombe simplement sur l'ancien comportement « dernier arrivé gagne ».
     * Le serveur le renvoie TOUJOURS.
     */
    updatedAt: z.string().optional(),
});
/**
 * Corps de `PATCH /api/app-settings` (superadmin) — PATCH partiel :
 *  - `aiEnabled`         : bascule le coupe-circuit IA (Incrément B) ;
 *  - `appName`           : renomme le site (LOT 3 ; trim/validation « non vide » dans le handler) ;
 *  - `accessGatewayUrl`  : passerelle (ADR-0008). `null` (ou chaîne vide/blanche, normalisée
 *    dans le handler) = DÉSACTIVER. Le schéma ne borne QUE la longueur : la validation
 *    réelle (http/https uniquement) vit dans le handler, `z.url()` n'excluant pas les
 *    schémas d'URL dangereux (`javascript:`…) — et un `.transform` casserait le boot (Zod v4).
 *  - `smallImageUpscalePercent` : plafond d'agrandissement des petites photos dans la
 *    visionneuse (entier `%`, borné `SMALL_IMAGE_UPSCALE_MIN/MAX_PERCENT`).
 */
export const updateAppSettingsSchema = z.object({
    aiEnabled: z.boolean().optional(),
    appName: z.string().min(1).max(APP_NAME_MAX_LENGTH).optional(),
    accessGatewayUrl: z.string().max(ACCESS_GATEWAY_URL_MAX_LENGTH).nullable().optional(),
    smallImageUpscalePercent: z
        .number()
        .int()
        .min(SMALL_IMAGE_UPSCALE_MIN_PERCENT)
        .max(SMALL_IMAGE_UPSCALE_MAX_PERCENT)
        .optional(),
    /**
     * VERROUILLAGE OPTIMISTE (facultatif) : l'`updatedAt` LU par le client. S'il est fourni
     * et ne correspond plus à l'`updated_at` en base, le PATCH est refusé en 409
     * `STALE_UPDATE` sans rien écrire. Absent ⇒ ancien comportement (dernier arrivé gagne).
     *
     * Borné en LONGUEUR seulement, sans `.datetime()` ni `.transform` : la validation réelle
     * (date analysable) vit dans le handler — le piège Zod v4 `z.toJSONSchema` interdit tout
     * `.transform` dans un schéma de corps de route (cf. `route-json-schema.test.ts`).
     */
    expectedUpdatedAt: z.string().min(1).max(64).optional(),
});
/**
 * Normalise/valide une URL de passerelle (ADR-0008). Source unique front + back :
 * l'API l'applique à l'écriture (`PATCH`), la SPA s'en sert en défense en profondeur
 * avant de NAVIGUER vers cette URL.
 *
 * Règles :
 *  - `null`, chaîne vide ou blanche → `null` = passerelle DÉSACTIVÉE (pas une erreur :
 *    vider le champ est la façon prévue de la couper) ;
 *  - `http:` / `https:` UNIQUEMENT — `z.url()` accepte des schémas dangereux
 *    (`javascript:`, `data:`), or cette URL finit dans un `window.location.assign()` ;
 *  - au-delà de la borne de longueur, ou non analysable → refus.
 *
 * Renvoie la chaîne TRIMÉE (pas de réécriture) : ce paquet est volontairement sans
 * `lib.dom`/types Node (contrats purs, `lib: ES2023`), donc pas de `new URL` ici —
 * l'allow-list de schéma est explicite, et `z.url()` couvre la forme.
 */
export function normalizeAccessGatewayUrl(raw) {
    if (raw === null)
        return { ok: true, value: null };
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return { ok: true, value: null };
    if (trimmed.length > ACCESS_GATEWAY_URL_MAX_LENGTH)
        return { ok: false };
    // Allow-list de SCHÉMA d'abord (c'est le contrôle de sécurité), forme ensuite.
    if (!/^https?:\/\//i.test(trimmed))
        return { ok: false };
    if (!z.url().safeParse(trimmed).success)
        return { ok: false };
    return { ok: true, value: trimmed };
}
//# sourceMappingURL=app-settings.js.map